import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { login } from '../api/auth';
import { fetchStores } from '../api/stores';
import { getErrorMessage } from '../api/client';
import { decodeJwtPayload } from '../utils/jwtDecode';

const POS_TOKEN_KEY = 'iconnectstore_pos_token';
const POS_STORE_KEY = 'iconnectstore_pos_store';
const POS_USERNAME_KEY = 'iconnectstore_pos_username';

export function getPosToken() {
  return sessionStorage.getItem(POS_TOKEN_KEY) || '';
}

export function getPosStoreId() {
  return sessionStorage.getItem(POS_STORE_KEY) || '';
}

export function getPosUsername() {
  return sessionStorage.getItem(POS_USERNAME_KEY) || 'User';
}

export function clearPosSession() {
  sessionStorage.removeItem(POS_TOKEN_KEY);
  sessionStorage.removeItem(POS_STORE_KEY);
  sessionStorage.removeItem(POS_USERNAME_KEY);
}

export function setPosSession(token, storeId, username) {
  sessionStorage.setItem(POS_TOKEN_KEY, token);
  sessionStorage.setItem(POS_STORE_KEY, storeId);
  sessionStorage.setItem(POS_USERNAME_KEY, username || '');
}

export default function POSLoginPage({ onLoggedIn }) {
  const [step, setStep] = useState('login'); // 'login' | 'store'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [storeId, setStoreId] = useState('');
  const [, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError('Username and password are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await login(username.trim(), password);
      const t = res?.token || '';
      if (!t) {
        setError('Login failed. No token received.');
        return;
      }
      setToken(t);
      setStep('store');
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  }, [username, password]);

  useEffect(() => {
    if (step !== 'store') return;
    fetchStores()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setStores(arr);
        const payload = decodeJwtPayload(token);
        const userStoreIds = Array.isArray(payload?.storeIds) ? payload.storeIds.map(String) : [];
        const isAdmin = payload?.isAdmin === true || payload?.role === 'admin';
        let filtered = arr;
        if (!isAdmin && userStoreIds.length > 0) {
          filtered = arr.filter((s) => userStoreIds.includes(String(s._id || s.id)));
        }
        setFilteredStores(filtered);
        if (filtered.length === 1) setStoreId(filtered[0]._id || filtered[0].id || '');
        else if (filtered.length > 0 && !storeId) setStoreId(filtered[0]._id || filtered[0].id || '');
      })
      .catch(() => setStores([]));
  }, [step, token, storeId]);

  const handleEnterPOS = useCallback(() => {
    if (!storeId) {
      setError('Please select a store.');
      return;
    }
    setPosSession(token, storeId, username.trim());
    if (onLoggedIn) onLoggedIn();
  }, [token, storeId, username, onLoggedIn]);

  const handleBackToLogin = useCallback(() => {
    setStep('login');
    setToken('');
    setStoreId('');
    setError('');
  }, []);

  return (
    <div className="min-h-screen bg-gray-800 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-6">
          <div className="mb-6 flex justify-center">
            <div className="rounded-xl bg-white p-2 shadow-md ring-1 ring-slate-200">
              <img src="/logo.png" alt="iConnect logo" className="h-12 w-auto max-w-[220px] object-contain" />
            </div>
          </div>

          {step === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter username"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2 px-4 rounded disabled:opacity-60"
              >
                {loading ? 'Signing in...' : 'Login'}
              </button>
            </form>
          )}

          {step === 'store' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Logged in as <strong className="text-gray-200">{username || 'User'}</strong></p>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Select store</label>
                <select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- Select store --</option>
                  {filteredStores.map((s) => (
                    <option key={s._id || s.id} value={s._id || s.id}>
                      {s.name || 'Unnamed store'}
                    </option>
                  ))}
                </select>
                {filteredStores.length === 0 && (
                  <p className="text-sm text-amber-400 mt-1">No stores available. Contact admin.</p>
                )}
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleEnterPOS}
                  disabled={!storeId || filteredStores.length === 0}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2 px-4 rounded disabled:opacity-60"
                >
                  Enter POS
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center mt-4">
          <Link to="/" className="text-indigo-400 hover:text-indigo-300 text-sm">
            ← Back to store
          </Link>
        </p>
      </div>
    </div>
  );
}
